import React, {useCallback, useEffect, useState, useRef} from 'react';
import { Container, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { API_URL } from "./lib/utils";
import UploadForm from './components/UploadForm';
import ProcessList from './components/ProcessList';
import { getSharedFile, clearSharedFile } from './components/ShareTargetHandler';
import { hasActiveProcesses } from './utils/processPolling';
import { createVisibilityAwarePoller } from './utils/processPoller';

const MAX_DISPLAYED_PROCESSES = 10;
const PROCESS_STEP_LABELS = [
    'Upload Audio',
    'Compress',
    'Transcribe',
    'Upload to Notion',
    'Summarize',
    'Generate Presentation',
    'Update Notion'
];
const PROCESS_POLL_INTERVAL_MS = 3000;

// Available summarization models
const SUMMARY_MODELS = [
    { id: 'gemini', name: 'Gemini 3.7 Flash' },
    { id: 'deepseek', name: 'DeepSeek R1' }
];

// Available summary detail levels
const DETAIL_LEVELS = [
    { id: 'normal', name: 'Normal' },
    { id: 'detailed', name: 'Detailed' }
];

const AudioProcessor = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchParams, setSearchParams] = useSearchParams();
    const sharedFileProcessedRef = useRef(false);
    const processFetchSequenceRef = useRef(0);
    const [file, setFile] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [language, setLanguage] = useState('hebrew');
    const [recordingName, setRecordingName] = useState("");
    const [selectedPrompt, setSelectedPrompt] = useState({
        _id: 'default',
        title: 'Default Prompt',
        content: 'Given the following transcription, Re-write the content in detailed structured way.'
    });
    const [outputLanguage, setOutputLanguage] = useState('hebrew');
    const [cropStart, setCropStart] = useState(0);
    const [cropEnd, setCropEnd] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const [shouldSummarize, setShouldSummarize] = useState(true);
    const [transcriptionService, setTranscriptionService] = useState('soniox');
    const [processes, setProcesses] = useState([]);
    const [selectedModels, setSelectedModels] = useState(['gemini']);
    const [isUploading, setIsUploading] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [detailLevel, setDetailLevel] = useState('normal');

    // Gamma presentation options
    const [shouldGeneratePresentation, setShouldGeneratePresentation] = useState(false);
    const [presentationNumCards, setPresentationNumCards] = useState(10);
    const [presentationTextAmount, setPresentationTextAmount] = useState('medium');

    const fetchProcesses = useCallback(async (signal) => {
        const requestSequence = ++processFetchSequenceRef.current;

        try {
            const response = await fetch(`${API_URL}/upload/processes?limit=${MAX_DISPLAYED_PROCESSES}`, {
                credentials: 'include',
                cache: 'no-store',
                signal,
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (response.status === 401) {
                if (!signal?.aborted) {
                    window.location.reload();
                }
                return;
            }
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const nextProcesses = await response.json();
            if (!signal?.aborted && requestSequence === processFetchSequenceRef.current) {
                setProcesses(nextProcesses);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error fetching processes:', error);
            }
        }
    }, []);

    // Handle ?shared=true query param (from service worker redirect after Share Target)
    useEffect(() => {
        const checkForSharedFile = async () => {
            const isShared = searchParams.get('shared') === 'true';

            if (isShared && !sharedFileProcessedRef.current) {
                sharedFileProcessedRef.current = true;

                const receivedFile = await getSharedFile();
                if (receivedFile) {
                    // Create URL for audio playback
                    const url = URL.createObjectURL(receivedFile);
                    setAudioUrl(url);
                    setFile(receivedFile);

                    // Get audio duration with proper cleanup
                    const audio = new Audio(url);
                    const handleMetadata = () => {
                        setAudioDuration(audio.duration);
                        setCropEnd(audio.duration);
                        // Clean up audio element after getting metadata
                        audio.removeEventListener('loadedmetadata', handleMetadata);
                        audio.src = '';
                    };
                    audio.addEventListener('loadedmetadata', handleMetadata);

                    // Set recording name from file name
                    const parts = receivedFile.name.split('.');
                    const fileName = parts.length > 1 ? parts.slice(0, -1).join('.') : receivedFile.name;
                    setRecordingName(fileName);

                    // Clear the shared file from service worker cache
                    await clearSharedFile();
                }
                // Remove the shared param from URL
                searchParams.delete('shared');
                setSearchParams(searchParams, { replace: true });
            }
        };

        checkForSharedFile();
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        const controller = new AbortController();
        fetchProcesses(controller.signal);

        return () => controller.abort();
    }, [fetchProcesses]);

    const shouldPollProcesses = hasActiveProcesses(processes);

    useEffect(() => {
        if (!shouldPollProcesses) return undefined;

        const poller = createVisibilityAwarePoller({
            poll: fetchProcesses,
            intervalMs: PROCESS_POLL_INTERVAL_MS
        });

        document.addEventListener('visibilitychange', poller.handleVisibilityChange);
        poller.start();

        return () => {
            poller.stop();
            document.removeEventListener('visibilitychange', poller.handleVisibilityChange);
        };
    }, [fetchProcesses, shouldPollProcesses]);

    const handleFileSelect = (selectedFile) => {
        setFile(selectedFile);
        
        if (selectedFile) {
            // Create URL for audio playback
            const url = URL.createObjectURL(selectedFile);
            setAudioUrl(url);
            
            // Get audio duration when file is selected
            const audio = new Audio(url);
            audio.onloadedmetadata = () => {
                setAudioDuration(audio.duration);
                setCropEnd(audio.duration);
            };

            // Always override recordingName with file name without extension
            const fileName = selectedFile.name.split('.').slice(0, -1).join('.');
            setRecordingName(fileName);
        } else {
            // File was removed - clear everything
            setAudioUrl(null);
            setAudioDuration(0);
            setCropStart(0);
            setCropEnd(0);
            setRecordingName(""); // Clear the recording name when file is deleted
        }
    };

    const handleLanguageChange = (event) => {
        const selectedLanguage = event.target.value;
        setLanguage(selectedLanguage);
        setOutputLanguage(selectedLanguage);
        if (selectedLanguage === 'english') {
            setTranscriptionService('soniox');
        } else if (selectedLanguage === 'hebrew') {
            setTranscriptionService('soniox');
        }
    };

    const handleRecordingNameChange = (event) => {
        setRecordingName(event.target.value);
    };

    const handleOutputLanguageChange = (event) => {
        setOutputLanguage(event.target.value);
    };

    const handleModelChange = (event) => {
        const { value, checked } = event.target;
        setSelectedModels(prev => {
            if (checked) {
                return [...prev, value];
            } else {
                // Don't allow unchecking the last model
                if (prev.length <= 1) {
                    return prev;
                }
                return prev.filter(model => model !== value);
            }
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isUploading) return; // Prevent multiple submissions
        
        if (cropStart >= cropEnd) {
            console.error('Invalid crop times');
            return;
        }
        if (!file || !language || !recordingName || (shouldSummarize && (!selectedPrompt || !selectedPrompt.content?.trim()))) {
            console.error('Please fill in all fields');
            return;
        }

        // Validate lastModified date
        const MIN_VALID_DATE = new Date('2010-01-01').getTime(); // arbitrary cutoff date
        const fileDate = file.lastModified && file.lastModified > MIN_VALID_DATE 
            ? file.lastModified 
            : Date.now();

        setIsUploading(true);

        try {
            // Step 1: Create the process first to get a processId
            // We'll only send the basic info needed to create a process record
            const formData = new FormData();
            formData.append('recordingName', recordingName);
            formData.append('shouldSummarize', shouldSummarize);
            formData.append('shouldGeneratePresentation', shouldGeneratePresentation);

            // Create process to get processId
            const initResponse = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                body: formData,
                credentials: 'include' // Ensure cookies are sent
            });
            
            if (initResponse.status === 401) {
                // Handle unauthorized - redirect to authentication
                window.location.reload(); // This will trigger AuthWrapper to show login
                return;
            }
            
            if (!initResponse.ok) {
                const body = await initResponse.json().catch(() => ({}));
                throw new Error(body.error || `HTTP error! status: ${initResponse.status}`);
            }
            
            const initData = await initResponse.json();
            console.log(initData.message);
            const processId = initData.processId;
            
            // Process created, now fetch the updated process list to show it in UI
            await fetchProcesses();

            // Step 2: Now upload the actual file with the processId
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            uploadFormData.append('language', language);
            uploadFormData.append('recordingName', recordingName);
            uploadFormData.append('promptContent', shouldSummarize ? (selectedPrompt?.content || '') : '');
            uploadFormData.append('lastModified', fileDate);
            uploadFormData.append('outputLanguage', outputLanguage);
            uploadFormData.append('cropStart', cropStart);
            uploadFormData.append('cropEnd', cropEnd);
            uploadFormData.append('shouldSummarize', shouldSummarize);
            uploadFormData.append('transcriptionService', transcriptionService);
            uploadFormData.append('selectedModels', JSON.stringify(selectedModels));
            uploadFormData.append('shouldGeneratePresentation', shouldGeneratePresentation);
            uploadFormData.append('presentationNumCards', presentationNumCards);
            uploadFormData.append('presentationTextAmount', presentationTextAmount);
            uploadFormData.append('detailLevel', detailLevel);

            // Add participants (filter out empty ones)
            const validParticipants = participants.filter(p => p.name.trim());
            if (validParticipants.length > 0) {
                uploadFormData.append('participants', JSON.stringify(validParticipants));
            }

            // Upload the file with the processId as a query parameter
            const uploadResponse = await fetch(`${API_URL}/upload?processId=${processId}`, {
                method: 'POST',
                body: uploadFormData,
                credentials: 'include' // Ensure cookies are sent
            });
            
            if (!uploadResponse.ok) {
                const body = await uploadResponse.json().catch(() => ({}));
                throw new Error(body.error || `HTTP error! status: ${uploadResponse.status}`);
            }
            
            const uploadData = await uploadResponse.json();
            console.log(uploadData.message);
            
            // Reset form after successful upload
            setFile(null);
            setAudioUrl(null);
            setRecordingName("");
            setCropStart(0);
            setCropEnd(0);
            setAudioDuration(0);
            await fetchProcesses();
        } catch (error) {
            console.error('Upload error:', error);
            
            // Check if it's a JSON parsing error (likely an authentication issue)
            if (error instanceof SyntaxError && error.message.includes('JSON')) {
                console.error('Authentication may have expired. Reloading page...');
                // Reload page to force authentication
                window.location.reload();
                return;
            }
            
            // Optionally display an error message to the user
            alert(`Upload failed: ${error.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <UploadForm 
                handleSubmit={handleSubmit}
                handleRecordingNameChange={handleRecordingNameChange}
                recordingName={recordingName}
                handleFileSelect={handleFileSelect}
                file={file}
                language={language}
                handleLanguageChange={handleLanguageChange}
                transcriptionService={transcriptionService}
                setTranscriptionService={setTranscriptionService}
                shouldSummarize={shouldSummarize}
                setShouldSummarize={setShouldSummarize}
                setSelectedPrompt={setSelectedPrompt}
                setShouldGeneratePresentation={setShouldGeneratePresentation}
                outputLanguage={outputLanguage}
                handleOutputLanguageChange={handleOutputLanguageChange}
                SUMMARY_MODELS={SUMMARY_MODELS}
                selectedModels={selectedModels}
                handleModelChange={handleModelChange}
                selectedPrompt={selectedPrompt}
                shouldGeneratePresentation={shouldGeneratePresentation}
                presentationNumCards={presentationNumCards}
                setPresentationNumCards={setPresentationNumCards}
                presentationTextAmount={presentationTextAmount}
                setPresentationTextAmount={setPresentationTextAmount}
                audioDuration={audioDuration}
                audioUrl={audioUrl}
                cropStart={cropStart}
                cropEnd={cropEnd}
                setCropStart={setCropStart}
                setCropEnd={setCropEnd}
                isUploading={isUploading}
                participants={participants}
                setParticipants={setParticipants}
                DETAIL_LEVELS={DETAIL_LEVELS}
                detailLevel={detailLevel}
                setDetailLevel={setDetailLevel}
            />
            <ProcessList 
                processes={processes}
                stepsArray={PROCESS_STEP_LABELS}
                isMobile={isMobile}
                MAX_DISPLAYED_PROCESSES={MAX_DISPLAYED_PROCESSES}
            />
        </Container>
    );
};

export default AudioProcessor;
